import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

export type JournalPost = {
  slug: string;
  title: string;
  description: string;
  excerpt: string;
  date: string; // ISO
  readingTime: string;
  category: string;
  body: () => ReactNode;
};

const P = ({ children }: { children: ReactNode }) => (
  <p className="mt-4 text-base leading-relaxed text-foreground/85">{children}</p>
);
const H2 = ({ children }: { children: ReactNode }) => (
  <h2 className="mt-12 font-display text-2xl md:text-3xl">{children}</h2>
);
const H3 = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-8 font-display text-xl">{children}</h3>
);
const UL = ({ children }: { children: ReactNode }) => (
  <ul className="mt-4 space-y-2 pl-5 text-base leading-relaxed text-foreground/85 [&>li]:list-disc">{children}</ul>
);
const Cta = ({ to, children }: { to: string; children: ReactNode }) => (
  <p className="mt-8 rounded-md border border-border bg-muted/40 p-6 text-base leading-relaxed text-foreground/85">
    {children}{" "}
    <Link to={to} className="font-medium text-bronze underline underline-offset-4">
      Continue &rarr;
    </Link>
  </p>
);

export const journalPosts: JournalPost[] = [
  {
    slug: "hair-toppers-vs-wigs",
    title: "Hair toppers vs. wigs: when to choose one over the other",
    description: "A guide to hair toppers for women with thinning hair — how they differ from wigs, when a topper is the right choice, and what to look for in one.",
    excerpt: "If you still have hair on the sides and back but the crown is thinning, a topper often reads more natural than a full wig — and lets you keep your bio hair around the perimeter.",
    date: "2025-11-08",
    readingTime: "6 min read",
    category: "Choosing a piece",
    body: () => (
      <>
        <P>
          The most common question we get is not <em>which wig</em>, it is <em>should this even be a wig?</em> For women whose hairline and perimeter are intact and whose thinning is concentrated on top, the honest answer is often no. A hair topper — a smaller piece that clips into your existing hair and covers only the crown — is usually the better call.
        </P>
        <P>
          Both toppers and wigs made from 100% virgin European hair can look identical at the strand level. What differs is coverage, wear time, and how the piece blends with your own hair. Here is how to tell which one is right for you.
        </P>

        <H2>What a topper actually is</H2>
        <P>
          A topper is a partial hairpiece with a small base — typically 6&times;6, 7&times;7, or 8&times;8 inches — worn on the crown of the head. It attaches with pressure-sensitive clips that grip your bio hair around the perimeter of the base. The rest of your hair (temples, sides, nape) stays visible and blends with the piece.
        </P>
        <P>
          A wig, by contrast, covers the entire scalp. Your own hair is either tucked away under a wig cap or pinned flat. A wig makes no attempt to blend with bio hair — it replaces it.
        </P>

        <H2>Choose a topper if:</H2>
        <UL>
          <li>Your thinning is concentrated on the crown, part, or hairline — but the sides and back still have density.</li>
          <li>You want to keep your own hairline visible and only supplement volume above it.</li>
          <li>You want the shortest, lightest possible piece and the fastest daily routine.</li>
          <li>You are in the early stages of hair loss and are not ready — practically or emotionally — for a full wig.</li>
        </UL>

        <H2>Choose a wig if:</H2>
        <UL>
          <li>You have advanced alopecia, are undergoing chemotherapy, or your hair is too sparse to hold clips comfortably.</li>
          <li>You cover for religious or modest reasons and need full coverage every day.</li>
          <li>You want a completely different length, color, or style than your own hair.</li>
          <li>You want the freedom to remove the whole piece at the end of the day without dealing with your own hair.</li>
        </UL>

        <H2>What to look for in a virgin European hair topper</H2>
        <P>
          The single most important factor in a topper is <em>how the base sits on your scalp</em>. A hand-tied silk or French lace top with a natural parting looks like scalp when the hair moves. Machine-wefted tops always read as a piece under strong light.
        </P>
        <P>
          Beyond construction, three things matter: the hair should be 100% virgin (unprocessed, cuticle-aligned root to tip); the density should be light-to-medium so it blends with fine bio hair rather than sitting on top of it; and the color should be root-shadowed to match how real hair grows out of the scalp — never a single flat tone from root to tip.
        </P>

        <H2>Living with a topper</H2>
        <P>
          A well-fitted topper takes about a minute to clip in each morning and is completely secure through a full day — including exercise, wind, and swimming with a cap. You wash it every 15 to 20 wears (or every 2 to 3 weeks with daily wear), and a virgin European hair topper properly cared for will last 3 to 5 years.
        </P>

        <Cta to="/shop">
          European Wig Luxury is releasing toppers alongside our short-hair collection in early 2026. In the meantime, our hand-tied wigs use the same virgin European hair and the same cap construction we use for toppers.
        </Cta>
      </>
    ),
  },

  {
    slug: "best-wigs-for-alopecia",
    title: "The best wigs for alopecia: what actually matters in a cap",
    description: "How to choose a wig if you have alopecia — cap construction, hair type, weight, breathability, and the small details that determine whether you'll wear it every day.",
    excerpt: "The right wig for alopecia is the one you'll actually wear every day — which comes down to how the cap sits on a sensitive scalp, not the hair itself.",
    date: "2025-11-10",
    readingTime: "7 min read",
    category: "Alopecia",
    body: () => (
      <>
        <P>
          When you have alopecia, the hair on your wig matters less than most people assume. The hair is what people see, but the cap is what you feel — every hour, every day, against a scalp that has no bio hair as a buffer. A beautiful wig on a stiff, poorly ventilated cap will sit in your closet within a month.
        </P>
        <P>
          Here is what actually matters if you are choosing a wig for alopecia areata, alopecia totalis, alopecia universalis, or frontal fibrosing alopecia — from most to least important.
        </P>

        <H2>1. Cap construction: fully hand-tied, always</H2>
        <P>
          There are three main construction types: machine-wefted (fast to make, stiff, hot), monofilament top with wefted back (common mid-tier), and fully hand-tied (every strand individually knotted by hand into a soft lace or silk cap).
        </P>
        <P>
          For alopecia, only fully hand-tied is comfortable long-term. The cap has no rigid weft tracks pressing against a bare scalp, and the base breathes. It also lets the hair move in every direction, so you can part it anywhere without seeing a hard seam.
        </P>

        <H2>2. Weight</H2>
        <P>
          A wig heavier than about 90 grams becomes noticeable after 4 to 6 hours of wear and creates a headache-inducing pressure line around the perimeter. Short wigs solve this: they carry less hair and less weight. A hand-tied short wig in virgin European hair typically weighs 60 to 80 grams — comfortable enough to forget about.
        </P>

        <H2>3. Hair type: virgin European, not processed</H2>
        <P>
          Processed hair (bleached, re-dyed, acid-bathed to remove cuticles) tangles, dries out, and needs replacement in 12 to 18 months. Virgin European hair — cuticles aligned root to tip, no chemical processing — moves like your own hair used to, and lasts 3 to 5 years with normal wear.
        </P>
        <P>
          Fine-to-medium European hair also matches the density most women had before alopecia. Coarser hair types over-densify and read as a wig.
        </P>

        <H2>4. The hairline and parting</H2>
        <P>
          A silk-top or French-lace parting looks like scalp. A hard-lace hairline with baby hairs pre-tied at natural angles looks like a real hairline. If either is missing, no amount of styling will fix it.
        </P>

        <H2>5. Adhesive vs. clips vs. suction</H2>
        <UL>
          <li><strong>Grip band</strong> (silicone or polyurethane strip inside the perimeter): the most comfortable for daily wear when you have some hair or peach fuzz to grip against. No adhesive, no residue.</li>
          <li><strong>Adhesive</strong> (medical glue or tape): most secure, best for active wear and total hair loss, but requires daily removal with solvent.</li>
          <li><strong>Suction cap</strong>: purpose-built for alopecia universalis — a silicone cap that grips a bare scalp without adhesive. Heavier, but the most secure hands-free option.</li>
        </UL>

        <H2>What to skip</H2>
        <UL>
          <li>Synthetic hair. It looks fine for the first month and quickly develops a plastic sheen no styling will hide.</li>
          <li>&quot;Human hair blends.&quot; The blend is almost always low-grade Asian hair over-processed to imitate European texture.</li>
          <li>Wigs with combs and clips as the only attachment method if you have no bio hair — they will not hold.</li>
        </UL>

        <H2>Medical wig funding</H2>
        <P>
          A prescription for a &quot;cranial prosthesis&quot; from your doctor allows many US insurance plans and HSA/FSA accounts to reimburse the cost. We provide the itemized invoice with the correct medical code on request.
        </P>

        <Cta to="/contact">
          Not sure which cap is right for your scalp? Send us a message and we'll walk you through options without pressure.
        </Cta>
      </>
    ),
  },

  {
    slug: "medical-wig-chemo",
    title: "Choosing a medical wig during chemo: fit, comfort, timing",
    description: "A practical guide to buying your first wig before or during chemotherapy — when to shop, how to match your natural hair, and what to prioritize when your scalp is sensitive.",
    excerpt: "Buy your wig two to three weeks before treatment starts, while you still have your natural hair to match against. The rest of this guide is about making the piece comfortable when your scalp turns tender.",
    date: "2025-11-12",
    readingTime: "6 min read",
    category: "Medical",
    body: () => (
      <>
        <P>
          If you have just been given a chemotherapy protocol likely to cause hair loss — anthracyclines, taxanes, most standard breast, ovarian, and lung regimens — the most useful thing you can do in the two weeks before treatment starts is buy your wig. Not during. Before.
        </P>
        <P>
          Here is what to prioritize, in the order it will matter.
        </P>

        <H2>Timing: two to three weeks before treatment</H2>
        <P>
          Hair typically starts falling out 14 to 21 days after the first chemo cycle. Buying beforehand gives you three advantages: your stylist can color-match against your real hair; you can practice putting the wig on while your scalp is still normal; and you have one less thing to arrange during a hard week.
        </P>

        <H2>Match your current hair, not your dream hair</H2>
        <P>
          The wigs people wear are the ones that look like their own hair. Match your natural color as closely as possible — including root shadow, not just length color. Match your natural length within two inches (shorter is usually easier). And match your natural density: chemo is not the moment to add volume you never had.
        </P>
        <P>
          The one exception: many women take chemo as an opportunity to try a shorter cut they would not otherwise. That is a good instinct — short wigs are lighter, cooler, and easier to put on with one hand.
        </P>

        <H2>Prioritize scalp comfort over everything</H2>
        <P>
          Chemo makes the scalp tender, dry, and often itchy. The cap must be:
        </P>
        <UL>
          <li><strong>Hand-tied.</strong> No weft tracks digging into a bare scalp.</li>
          <li><strong>Lightweight.</strong> Under 90 grams total.</li>
          <li><strong>Adjustable at the nape.</strong> Head size fluctuates during treatment; a wig that fits perfectly at week 1 will feel loose or tight by week 6.</li>
          <li><strong>Silk-lined perimeter</strong> where possible. Softer than lace against sensitive skin.</li>
        </UL>

        <H2>Human hair vs. synthetic during chemo</H2>
        <P>
          Both have a place. Synthetic wigs hold their style through the day and cost less — reasonable for a 4 to 6 month treatment window. Human hair wigs (especially virgin European) feel and move like real hair, can be styled with heat, and last for years after treatment ends if you want to keep wearing them.
        </P>
        <P>
          Many women buy one of each: a synthetic for daily wear during treatment (easy, wash-and-go), and a human hair piece for occasions and life after chemo.
        </P>

        <H2>The prescription — get it in writing</H2>
        <P>
          Ask your oncologist for a written prescription for a &quot;cranial prosthesis for chemotherapy-induced alopecia.&quot; It should specify the diagnosis code and the words <em>cranial prosthesis</em>, not <em>wig</em>. This is what unlocks insurance reimbursement, HSA/FSA coverage, and in some cases sales tax exemption. We provide the itemized invoice on request.
        </P>

        <H2>After treatment</H2>
        <P>
          Regrowth typically starts 3 to 6 months after the last cycle and full length returns over 12 to 18 months. Many women keep wearing their wig well past that point — regrown chemo hair is often a different texture, and having the option to cover on days it does not cooperate is a real quality-of-life gain.
        </P>

        <Cta to="/contact">
          We work with women preparing for treatment every week. Reach out — every message is answered personally and in confidence.
        </Cta>
      </>
    ),
  },

  {
    slug: "hand-tied-vs-machine-wefted",
    title: "Hand-tied vs. machine-wefted wigs: what changes day-to-day",
    description: "The difference between hand-tied and machine-wefted wig construction — how each feels, moves, lasts, and what you're actually paying for at each tier.",
    excerpt: "Machine-wefted wigs are 10&times; faster to make and cost a fraction as much. What you give up is not obvious in a photo. It becomes obvious around hour four of wearing one.",
    date: "2025-11-15",
    readingTime: "5 min read",
    category: "Construction",
    body: () => (
      <>
        <P>
          Every wig on the market is built one of two ways: hair is either sewn onto machine-made strips called wefts and stitched into a cap (fast, cheap), or every single strand is knotted by hand into a soft lace or silk base (slow, expensive). Everything else — hair quality, hair length, cap size — is a variable on top of that one architectural choice.
        </P>
        <P>
          Here is what actually differs in daily wear.
        </P>

        <H2>Weight and feel on the scalp</H2>
        <P>
          A machine-wefted wig has 8 to 12 horizontal weft tracks — visible ridges of stitched hair — running around the cap. They are rigid, they hold heat, and they press into the scalp along fixed lines. Most people notice this as a pressure headache after 4 to 6 hours.
        </P>
        <P>
          A hand-tied wig has no wefts. The base is one continuous piece of soft lace or silk, and the hair is distributed evenly across it. It sits on the head the way a swim cap sits on the head — even pressure, no ridges.
        </P>

        <H2>How the hair moves</H2>
        <P>
          On a wefted wig, the hair falls in the direction the weft was sewn. You can part it where the weft allows and nowhere else. Change your part and you see a hard track underneath.
        </P>
        <P>
          On a hand-tied wig, every strand is knotted individually and moves independently. You can part it anywhere — down the middle, off to the side, zigzag — and it looks like it grew that way. This is the single biggest reason hand-tied wigs read as real hair.
        </P>

        <H2>Breathability</H2>
        <P>
          Hand-tied lace is essentially mesh. Air moves through it. In summer, the difference is significant — a wefted cap becomes a hat you cannot take off.
        </P>

        <H2>Longevity</H2>
        <P>
          A well-made machine-wefted wig in good human hair lasts 12 to 18 months of daily wear. A hand-tied wig in virgin European hair lasts 3 to 5 years. Over the lifetime of one hand-tied piece, you would buy three or four machine-wefted equivalents.
        </P>

        <H2>Price, honestly</H2>
        <P>
          Hand-tied construction takes 40 to 60 hours per wig. Machine-wefted construction takes 3 to 5 hours. That is the entire price difference — the hair on both can be identical.
        </P>
        <P>
          Whether it is worth it is a real question. If you wear a wig occasionally or for short stretches, machine-wefted is a defensible choice. If you wear a wig every day for medical, religious, or lifestyle reasons, hand-tied pays back on comfort within the first month and on cost within the second year.
        </P>

        <H2>How to spot the difference</H2>
        <P>
          Turn the wig inside out. If you see horizontal stripes of stitching, it is wefted. If you see a soft mesh base with tiny individual knots visible, it is hand-tied. Anything called &quot;mono top&quot; or &quot;silk top&quot; is hand-tied at the parting only — a middle tier.
        </P>

        <Cta to="/shop">
          Every European Wig Luxury piece is fully hand-tied. See the current collection.
        </Cta>
      </>
    ),
  },

  {
    slug: "what-is-virgin-european-hair",
    title: "What \"virgin European hair\" really means",
    description: "The virgin European hair label is used loosely across the wig industry. Here's what it should mean, how to verify it, and why the distinction matters for how a wig looks and lasts.",
    excerpt: "Almost every luxury wig brand claims virgin European hair. Very few actually source it. Here's how to tell the difference — and why it changes how the wig moves, ages, and lasts.",
    date: "2025-11-17",
    readingTime: "6 min read",
    category: "Sourcing",
    body: () => (
      <>
        <P>
          &quot;Virgin European hair&quot; is the most abused label in the wig industry. It should mean three specific things at once: the hair is European in origin, virgin in condition, and cuticle-aligned in construction. In practice, most pieces sold under this name meet only one of those, if any.
        </P>
        <P>
          Here is what each word should actually mean, and how to check.
        </P>

        <H2>European</H2>
        <P>
          European hair is fine-to-medium in diameter, naturally ranges from platinum blonde through light and dark brunette to occasional natural red, and has a subtle wave pattern in most donors. It is genetically distinct from Asian hair (thicker, coarser, straight) and from most Indian temple hair (medium diameter, black, coarser cuticle).
        </P>
        <P>
          Real European hair is scarce. It is collected in small quantities from long-hair donors in Eastern Europe, Russia, and parts of Italy and Spain. Global supply is a tiny fraction of the wig market. If a brand is selling &quot;European hair&quot; at volume for a few hundred dollars a piece, it is almost certainly Indian or Chinese hair processed to match European texture and color.
        </P>

        <H2>Virgin</H2>
        <P>
          Virgin means the hair has not been chemically treated after being cut from the donor. No bleaching, no re-dyeing, no perming, no relaxing, and — critically — no acid bath.
        </P>
        <P>
          The acid bath is the industry secret. Most &quot;human hair&quot; wigs are stripped of their cuticles in an acid dip to make sure all the strands lie in the same direction. It works, but it destroys the hair's structural integrity. The strand becomes porous, dry, and dependent on the silicone coating manufacturers apply to fake shine. That coating washes off after 6 to 8 shampoos and the hair turns to straw.
        </P>
        <P>
          Truly virgin hair does not need cuticle stripping because it was collected with the cuticles already aligned at the donor (see next section).
        </P>

        <H2>Cuticle-aligned</H2>
        <P>
          Every strand of hair grows out of the scalp in one direction — cuticle scales pointing away from the root, toward the tip. When two strands are stored root-to-tip in opposite directions, their cuticles clash and the hair tangles.
        </P>
        <P>
          Cuticle-aligned hair is collected in a ponytail from a single donor, kept in root-to-tip orientation through every step of processing, and knotted into the wig in that same orientation. It moves in one direction. It reflects light evenly. It does not tangle.
        </P>
        <P>
          Mass-market &quot;human hair&quot; wigs are made from mixed collections gathered off salon floors — hair pointing in all directions. Those wigs get an acid bath to compensate. Cuticle-aligned virgin hair is the only way to get natural movement and long lifespan in one piece.
        </P>

        <H2>How to verify what you are buying</H2>
        <UL>
          <li><strong>Ask where the hair was sourced.</strong> A serious brand can name a region and describe how it was collected. Vagueness is a signal.</li>
          <li><strong>Ask if the hair was acid-bathed.</strong> Anyone selling truly virgin cuticle-aligned hair will say no immediately.</li>
          <li><strong>Look at the color palette.</strong> Virgin European hair comes in natural shades — dishwater blondes, mid-browns, natural black is rare. A brand offering hot pink and platinum white as core inventory is selling processed hair.</li>
          <li><strong>Feel a strand between wet fingers.</strong> Virgin hair has friction in one direction (tip to root) and glides in the other. Processed hair glides in both — the cuticles are gone.</li>
        </UL>

        <H2>Why it matters day-to-day</H2>
        <P>
          A virgin cuticle-aligned European hair wig, well cared for, moves and reflects light like the hair the donor grew. It handles heat, weather, and washing without losing its personality. It lasts 3 to 5 years of daily wear. A processed &quot;human hair&quot; wig is a 12 to 18 month product with an increasingly plastic sheen.
        </P>
        <P>
          The premium is real, and so is the difference.
        </P>

        <Cta to="/about">
          Our sourcing and standards in full.
        </Cta>
      </>
    ),
  },

  {
    slug: "care-for-human-hair-wig",
    title: "How to care for a human hair wig",
    description: "A no-nonsense care routine for virgin human hair wigs: washing, conditioning, storing, and the small habits that decide whether your wig lasts one year or five.",
    excerpt: "The rules are simple and the difference between following them and not is 2&times; to 3&times; the lifespan of your wig.",
    date: "2025-11-20",
    readingTime: "5 min read",
    category: "Care",
    body: () => (
      <>
        <P>
          A virgin European hair wig is more forgiving than most owners give it credit for — but it responds badly to two things: friction on a dry scalp cap and heat without protection. Handle those two, and everything else is optional polish.
        </P>

        <H2>How often to wash</H2>
        <UL>
          <li><strong>Daily wear:</strong> every 15 to 20 wears, or roughly every 2 to 3 weeks.</li>
          <li><strong>Occasional wear:</strong> every 10 to 15 wears.</li>
          <li><strong>Between washes:</strong> a light dry shampoo at the roots is fine. Do not spray it on the cap itself.</li>
        </UL>
        <P>
          Washing more often is not safer — it dries out the hair. Washing less often lets sweat and product residue break down the cap.
        </P>

        <H2>How to wash</H2>
        <ol className="mt-4 space-y-3 pl-5 text-base leading-relaxed text-foreground/85 [&>li]:list-decimal">
          <li>Brush the wig gently from tip to root before wetting — never after. Wet hair stretches and snaps.</li>
          <li>Fill a sink with cool-to-lukewarm water (never hot). Add a small amount of sulfate-free shampoo.</li>
          <li>Submerge the wig and press the water through — do not scrub, twist, or wring.</li>
          <li>Rinse under cool running water in the direction of hair growth (root to tip).</li>
          <li>Apply conditioner to the mid-lengths and ends only. Never on the cap or knots — it loosens them.</li>
          <li>Rinse thoroughly. Blot with a microfiber towel. Do not rub.</li>
          <li>Air dry on a wig stand or a rolled-up towel. Never on a foam head — foam stretches the cap.</li>
        </ol>

        <H2>Products to use — and skip</H2>
        <UL>
          <li><strong>Use:</strong> sulfate-free shampoo, silicone-free conditioner, light argan or jojoba oil on the ends, a wide-tooth wooden comb, a boar-bristle brush for styling.</li>
          <li><strong>Skip:</strong> anything with sulfates, heavy silicones, or protein reconstructors — the hair is not attached to a scalp that can regenerate. Skip alcohol-based hairsprays and dry shampoos with talc.</li>
        </UL>

        <H2>Heat</H2>
        <P>
          Virgin hair takes heat well but only with protection. Apply a heat protectant on damp hair before drying. Keep dryers on medium and never point heat at the cap or the knots — you'll melt the base. Curling irons at 300&deg;F (150&deg;C) and flat irons at 320&deg;F (160&deg;C) are the ceiling for fine European hair.
        </P>

        <H2>Storing</H2>
        <P>
          When you are not wearing your wig, store it on a canvas wig block (not foam) in a cool dark drawer or the box it came in. Direct sunlight fades natural pigment. Long-term humidity encourages mildew at the cap.
        </P>

        <H2>What shortens a wig's life fastest</H2>
        <UL>
          <li>Sleeping in it. Friction against a pillow tangles the hair and wears the cap edge.</li>
          <li>Swimming in chlorine or salt water without rinsing immediately after.</li>
          <li>Storing wet or damp.</li>
          <li>Brushing wet.</li>
          <li>Using drugstore shampoo with sulfates.</li>
        </UL>

        <H2>When to send it in for service</H2>
        <P>
          Every 12 to 18 months, a virgin hair wig benefits from professional cleaning and, if needed, minor knot repair. Do not attempt to re-knot or re-color at home — both are irreversible.
        </P>

        <Cta to="/care">
          Our full care page walks through this with photos and answers common questions.
        </Cta>
      </>
    ),
  },
];

export function getPost(slug: string): JournalPost | undefined {
  return journalPosts.find((p) => p.slug === slug);
}
